import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import {
  comparisonFigures, isObjectId, isPhoneFormat, matchLabel, mergeComparison, normaliseObjectId, normalisePhone,
  PHONE_FORMAT_HINT, userFieldForError, usersCardView,
} from "../lib/eam";
import type { EamComparisonRow, EamEntraSource, EamPhoneSource, EamUser, EamUserList } from "../types/api";

const CARD = "bg-white rounded-xl shadow-card border border-gray-100 p-6";
const LINK = "text-sm font-medium text-accent hover:text-accent-dark disabled:opacity-50";
const BUTTON =
  "inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-subtle text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

function Badge({ children }: { children: string }) {
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{children}</span>
  );
}

function FieldError({ text }: { text: string | null }) {
  return text ? <p className="mt-1 text-xs text-red-600">{text}</p> : null;
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : "Something went wrong.";
}

interface Props {
  onUsers?: (users: EamUser[]) => void;
  phoneSource: EamPhoneSource | null;
  compareSource: EamEntraSource | null;
  /** Changes when what the comparison depends on (the read access) changes: it is fetched again. */
  comparisonKey: string;
  onReadAccessStale?: () => void;
}

// rows null: the call failed, or read access is missing or not known (an empty tenant gives []).
interface Answer {
  source: EamEntraSource;
  list: EamUserList;
  key: string;
  rows: EamComparisonRow[] | null;
}

export default function EamUsersCard({ onUsers, phoneSource, compareSource, comparisonKey, onReadAccessStale }: Props) {
  const [list, setList] = useState<EamUserList | null>(null);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [confirmRemoveMatching, setConfirmRemoveMatching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [objectId, setObjectId] = useState("");
  const [phone, setPhone] = useState("");
  const [addErrors, setAddErrors] = useState<{ object_id: string | null; phone: string | null }>({
    object_id: null,
    phone: null,
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [editPhone, setEditPhone] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [removeId, setRemoveId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await api.listEamUsers());
      setError(null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional fetch on mount, like the page
    void load();
  }, [load]);

  // Not a dependency of load: that would refetch whenever the parent re-renders.
  useEffect(() => {
    if (list) onUsers?.(list.users);
  }, [list, onUsers]);

  // An answer that arrives after the source, the list or the key changed is dropped.
  useEffect(() => {
    if (!compareSource || !list) return;
    let current = true;
    api.compareEamUsers(compareSource).then((a) => a.users, () => null).then((rows) => {
      if (current) setAnswer({ source: compareSource, list, key: comparisonKey, rows });
    });
    return () => {
      current = false;
    };
  }, [compareSource, list, comparisonKey]);

  const users = list?.users ?? [];
  const settled = !!compareSource && answer?.source === compareSource && answer.list === list && answer.key === comparisonKey;
  const merged = settled && answer ? mergeComparison(users, answer.rows) : null;
  const known = settled && answer?.rows != null;
  const view = usersCardView(phoneSource, compareSource, known ? merged : null, users.length, settled && !known);
  const figures = known && merged ? comparisonFigures(merged) : null;
  const rowOf = new Map((merged ?? []).map((m) => [m.user.id, m.row]));
  const limit = list?.limit ?? 100;
  const full = !!list && users.length >= limit;
  const objectIdOk = isObjectId(normaliseObjectId(objectId));
  const phoneOk = isPhoneFormat(phone);

  const add = async () => {
    setBusy(true);
    setError(null);
    setAddErrors({ object_id: null, phone: null });
    try {
      await api.addEamUser(normaliseObjectId(objectId), normalisePhone(phone));
      setObjectId("");
      setPhone("");
      await load();
    } catch (e) {
      // Reload first: load() clears the error, and the limit message must stay.
      if (e instanceof ApiError && e.error === "user_limit") await load();
      const field = e instanceof ApiError ? userFieldForError(e.error) : null;
      if (field) setAddErrors({ object_id: null, phone: null, [field]: errorText(e) });
      else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  // user_not_found: the row is already gone on the server, so show the list as it is now.
  const act = async (action: () => Promise<unknown>, onField?: (m: string) => void) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setEditId(null);
      setRemoveId(null);
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.error === "user_not_found") {
        setEditId(null);
        setRemoveId(null);
        await load();
      } else if (onField && e instanceof ApiError && userFieldForError(e.error) === "phone") {
        onField(errorText(e));
      } else {
        setError(errorText(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const removeMatching = () =>
    act(async () => {
      try {
        await api.removeMatchingEamUsers();
      } catch (e) {
        if (e instanceof ApiError && e.status === 409 && e.error === "read_access_missing") onReadAccessStale?.();
        // Some users may be removed already: reload first, since load() clears the error that act() sets next.
        await load();
        throw e;
      } finally {
        setConfirmRemoveMatching(false);
      }
    });

  const entraNumber = (row: EamComparisonRow | undefined): string => {
    if (!settled) return "…";
    if (!row || !known || row.status === "unknown") return "Not known";
    return row.entra_phone ?? "No number";
  };

  const startEdit = (u: EamUser) => {
    setEditId(u.id);
    setEditPhone(u.phone ?? "");
    setEditError(null);
    setRemoveId(null);
  };

  return (
    <div className={`${CARD} mt-6`}>
      <div className="flex items-baseline justify-between">
        <h3 className="text-base font-semibold text-gray-900">{view.title}</h3>
        {list && (
          <span className="text-sm text-gray-500">
            {users.length} of {limit}
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 mt-3" role="alert">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-gray-500">Loading...</p>
      ) : (
        <>
          {figures && (
            <p className="mt-4 text-sm text-gray-500">
              <span className="font-semibold text-gray-900">{figures.listed}</span> listed
              {" · "}
              <span className="font-semibold text-gray-900">{figures.differ}</span> differ
              {" · "}
              <span className="font-semibold text-gray-900">{figures.notInTenant}</span> not in tenant
            </p>
          )}
          {view.removeMatching !== null && (
            <div className="mt-3 flex items-center gap-3 text-sm">
              {confirmRemoveMatching ? (
                <>
                  <span className="text-gray-700">Remove the {view.removeMatching} that match Entra?</span>
                  <button className={LINK} disabled={busy} onClick={() => void removeMatching()}>
                    Yes
                  </button>
                  <button className={LINK} disabled={busy} onClick={() => setConfirmRemoveMatching(false)}>
                    No
                  </button>
                </>
              ) : (
                <button className={LINK} disabled={busy} onClick={() => setConfirmRemoveMatching(true)}>
                  Remove the {view.removeMatching} that match Entra
                </button>
              )}
            </div>
          )}
          {view.emptyLine && <p className="mt-4 text-sm text-gray-700">{view.emptyLine}</p>}
          {users.length > 0 && (
            <div className="mt-4 divide-y divide-gray-100 text-sm">
              <div className="flex gap-3 pb-2 text-xs uppercase tracking-wide text-gray-400">
                <span className="w-80 flex-shrink-0">{view.comparing ? "User" : "Object ID"}</span>
                {view.comparing ? (
                  <>
                    <span className="w-40 flex-shrink-0">Number on this page</span>
                    <span className="w-40 flex-shrink-0">Number in Entra</span>
                    <span>Status</span>
                  </>
                ) : (
                  <>
                    <span className={view.entraColumn ? "w-40 flex-shrink-0" : undefined}>Mobile number</span>
                    {view.entraColumn && <span>Number in Entra</span>}
                  </>
                )}
              </div>
              {users.map((u) => {
                const row = rowOf.get(u.id);
                const name = known ? (row?.display_name ?? null) : null;
                const upn = known ? (row?.upn ?? null) : null;
                return (
                  <div key={u.id} className="flex flex-wrap items-center gap-3 py-2.5">
                    {view.comparing && (name || upn) ? (
                      <span className="w-80 flex-shrink-0 break-all text-gray-800">
                        {name ?? upn}
                        {name && upn && <span className="block text-xs text-gray-500">{upn}</span>}
                      </span>
                    ) : (
                      <span className="w-80 flex-shrink-0 break-all font-mono text-gray-800">{u.object_id}</span>
                    )}
                    {editId === u.id ? (
                      <div className="flex min-w-0 flex-1 flex-wrap items-start gap-2">
                        <div>
                          <input
                            className="input font-mono"
                            aria-label="Mobile number"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            autoComplete="off"
                          />
                          <p className={`mt-1 text-xs ${editPhone && !isPhoneFormat(editPhone) ? "text-red-600" : "text-gray-500"}`}>
                            {PHONE_FORMAT_HINT}
                          </p>
                          <FieldError text={editError} />
                        </div>
                        <button
                          className={LINK}
                          disabled={busy || !isPhoneFormat(editPhone)}
                          onClick={() => void act(() => api.changeEamUser(u.id, normalisePhone(editPhone)), setEditError)}
                        >
                          Save
                        </button>
                        <button className={LINK} disabled={busy} onClick={() => setEditId(null)}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className={view.entraColumn ? "w-40 flex-shrink-0" : undefined}>
                          {u.phone ? (
                            <span className="font-mono text-gray-800">{u.phone}</span>
                          ) : (
                            <span className="text-gray-400">No number</span>
                          )}
                        </span>
                        {view.entraColumn && (
                          <span className={`${view.comparing ? "w-40 flex-shrink-0 " : ""}font-mono text-gray-800`}>{entraNumber(row)}</span>
                        )}
                        {view.comparing && settled && <Badge>{matchLabel(known && row ? row.status : "unknown")}</Badge>}
                        {!u.enabled && <Badge>Disabled</Badge>}
                        {u.other_tenant && <Badge>Other tenant</Badge>}
                        <span className="ml-auto flex items-center gap-3">
                          {removeId === u.id ? (
                            <>
                              <span className="text-gray-700">Remove this user?</span>
                              <button
                                className={LINK}
                                disabled={busy}
                                onClick={() => void act(() => api.removeEamUser(u.id))}
                              >
                                Yes
                              </button>
                              <button className={LINK} disabled={busy} onClick={() => setRemoveId(null)}>
                                No
                              </button>
                            </>
                          ) : (
                            <>
                              <button className={LINK} disabled={busy} onClick={() => startEdit(u)}>
                                Edit
                              </button>
                              <button
                                className={LINK}
                                disabled={busy}
                                onClick={() => {
                                  setRemoveId(u.id);
                                  setEditId(null);
                                }}
                              >
                                Remove
                              </button>
                            </>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 border-t border-gray-100 pt-4">
            {full ? (
              <p className="text-sm text-gray-500">The list holds at most {limit} users.</p>
            ) : (
              <form
                className="flex flex-wrap items-start gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (objectIdOk && phoneOk && !busy) void add();
                }}
              >
                <div className="w-80">
                  <input
                    className="input font-mono"
                    placeholder="Object ID"
                    aria-label="Object ID"
                    value={objectId}
                    onChange={(e) => setObjectId(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <p className="mt-1 text-xs text-gray-500">Entra admin center → Users → the user → Object ID</p>
                  <FieldError text={addErrors.object_id} />
                </div>
                <div className="w-56">
                  <input
                    className="input font-mono"
                    placeholder="Mobile number"
                    aria-label="Mobile number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="off"
                  />
                  <p className={`mt-1 text-xs ${phone && !phoneOk ? "text-red-600" : "text-gray-500"}`}>
                    {PHONE_FORMAT_HINT}
                  </p>
                  <FieldError text={addErrors.phone} />
                </div>
                <button type="submit" className={BUTTON} disabled={busy || !objectIdOk || !phoneOk}>
                  Add user
                </button>
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}
